import type { Component, ConfigState, OrientedComponents } from '@renderer/types/configState'

/**
 * A hub is its own Mongo collection, not a Project (`GET /api/hubs?id=`).
 *
 * It owns exactly two screens — a Welcome and the mode picker — plus the theme
 * they render with. It has no packages, frames, filters, capture or print
 * settings, because a hub never runs a session: the guest picks a member and
 * the MEMBER project's own config drives everything after that.
 */
export interface HubMember {
  projectId: string
  label: string
  description?: string
  thumbnailUrl?: string
  order?: number
  isActive?: boolean
  /**
   * Display-only snapshot of the member Project, stamped by Studio's GET. The
   * amount charged is always the member Checkout's, never this card.
   */
  resolved?: {
    title?: string
    thumbnail?: string
    customPath: string | null
    currency?: string
    startingPrice: number | null
    isActive?: boolean
  }
}

export interface HubDocument {
  _id: string
  title: string
  thumbnail?: string
  owner?: string
  isActive?: boolean
  /**
   * Typed as the renderer's currency union rather than a bare string so hub
   * prices go through the same `formatAmount` path as a project's — the wire
   * value is stamped by the same Studio helper that stamps a Project's.
   */
  resolvedCurrency?: ConfigState['resolvedCurrency']
  theme?: ConfigState['theme']
  screenSize?: ConfigState['screenSize']
  screenSizes?: ConfigState['screenSizes']
  welcome?: {
    name?: string
    settings?: { tapAnywhereToStart?: boolean }
    components?: Component[]
    orientedComponents?: OrientedComponents<Component>
  }
  modeSelect?: {
    title?: string
    subtitle?: string
    ctaLabel?: string
    modeSelectTimeout?: number
    returnTo?: 'welcome' | 'mode'
    members?: HubMember[]
  }
  updatedAt?: string
}

/** Canvas a hub falls back to when Studio has not stamped one. */
const DEFAULT_HUB_SCREEN_SIZE: ConfigState['screenSize'] = { width: 1080, height: 1920 }

const DEFAULT_HUB_THEME: ConfigState['theme'] = {
  primaryColor: '#000000',
  secondaryColor: '#ffffff',
  backgroundColor: '#ffffff',
  fontFamily: 'Inter'
}

/**
 * Project a hub document onto `ConfigState` so `View` renders it unchanged.
 *
 * The renderer reads one shape — `config.pages.<page>.settings.enabled` — for
 * every routing decision it makes, so the cheapest way to run a hub through it
 * is to hand it a config in which only Welcome and the picker are on. Every
 * other page is a DISABLED stub carrying the minimum its settings type
 * requires: a hub has no catalogue (`/api/filters|frames|stickers?projectId=`
 * returns nothing for a hub id) and no session, so a page that somehow rendered
 * would fetch against an id no Project owns.
 *
 * `pages.welcome.settings.customPath` is always null: a hub renders the default
 * Welcome page set. That is what removes the "the hub's own brand page set has
 * no picker" class of bug that the Project-shaped hub had — the brand page sets
 * belong to the MEMBERS.
 */
export function hubToConfig(hub: HubDocument): ConfigState {
  const members: HubMember[] = Array.isArray(hub.modeSelect?.members)
    ? (hub.modeSelect?.members as HubMember[])
    : []

  return {
    _id: hub._id,
    title: hub.title,
    thumbnail: hub.thumbnail ?? '',
    theme: hub.theme ?? DEFAULT_HUB_THEME,
    screenSize: hub.screenSize ?? DEFAULT_HUB_SCREEN_SIZE,
    screenSizes: hub.screenSizes,
    resolvedCurrency: hub.resolvedCurrency,
    updatedAt: hub.updatedAt,
    pages: {
      welcome: {
        name: hub.welcome?.name || 'Welcome',
        settings: {
          enabled: true,
          // A hub never charges and never runs a brand page set.
          startWithPayment: false,
          customPath: null,
          tapAnywhereToStart: hub.welcome?.settings?.tapAnywhereToStart
        },
        components: hub.welcome?.components,
        orientedComponents: hub.welcome?.orientedComponents
      },
      modeSelect: {
        name: 'Mode picker',
        settings: {
          enabled: true,
          title: hub.modeSelect?.title,
          subtitle: hub.modeSelect?.subtitle,
          ctaLabel: hub.modeSelect?.ctaLabel,
          modeSelectTimeout: hub.modeSelect?.modeSelectTimeout,
          returnTo: hub.modeSelect?.returnTo,
          members
        }
      },
      filter: {
        name: 'Filter',
        settings: {
          enabled: false,
          filterTimeout: 0,
          filterIds: [],
          useLivePreview: false
        }
      },
      frame: {
        name: 'Frame',
        settings: {
          enabled: false,
          frameTimeout: 0,
          frameIds: [],
          frameCategories: [],
          categoryModeEnabled: false
        }
      },
      checkout: {
        name: 'Checkout',
        settings: {
          enabled: false,
          checkoutTimeout: 0,
          promoEnabled: false,
          packages: [],
          promos: []
        }
      },
      payment: {
        name: 'Payment',
        settings: { enabled: false, paymentTimeout: 0 }
      },
      capture: {
        name: 'Capture',
        settings: {
          enabled: false,
          prepTimeout: 0,
          captureCountdown: 0,
          captureCount: 0,
          mode: 'capture',
          gifEnabled: false,
          gifSpeed: 0,
          customGifFrame: '',
          recordingEnabled: false,
          recordingSpeed: 0,
          captureTimeout: 0
        }
      },
      record: {
        name: 'Record',
        settings: { enabled: false, prepTimeout: 0, recordingTimeout: 0, audioEnabled: false }
      },
      retake: {
        name: 'Retake',
        settings: {
          enabled: false,
          maxRetakeCount: 0,
          maxRetakeSession: 0,
          retakeTimeout: 0
        }
      },
      select: {
        name: 'Select',
        settings: { enabled: false, selectionTimeout: 0 }
      },
      result: {
        name: 'Result',
        settings: { enabled: false, resultTimeout: 0, askUserConsent: false }
      },
      camera: {
        name: 'Camera',
        settings: { enabled: false, cameraTimeout: 0 }
      },
      edit: {
        name: 'Edit',
        settings: { enabled: false, editTimeout: 0, stickerIds: [] }
      }
    }
  }
}
